import React from "react"
import types from "prop-types"
import { MenuButton } from "@szhsin/react-menu"
import { BroadcastChannel } from "broadcast-channel"
import { useSelector } from "react-redux"
import {
  currentUserId as getCurrentUserId,
  currentUserIsInAdminRole as getCurrentUserIsInAdminRole,
  currentUserIsPhpAdmin as getCurrentUserIsPhpAdmin,
  impersonationInProgress as getImpersonationInProgress,
} from "reduxSlices/sessionSlice"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import * as API from "services/api"
import { IMPERSONATION_BY_ORGANIZATION_ADMINS_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import { IMPERSONATION, START_IMPERSONATION } from "utils/impersonation"
import { SUBMITTER_ROLE } from "utils/userRoleHelpers"
import ExpandMenu, { ExpandMenuItem } from "shared/ExpandMenu"
import { errorToast, successToast } from "shared/toast"

const impersonationsChannel = new BroadcastChannel(IMPERSONATION)

const impersonateUser = async (user) => {
  const response = await API.startImpersonation({ impersonateeId: user.id })

  if (response.ok) {
    impersonationsChannel.postMessage(START_IMPERSONATION)
    successToast(`${user.fullName} successfully impersonated!`)
  } else {
    console.error("Error impersonating: ", response)
    errorToast("Something went wrong. Unable to impersonate user.", response)
  }
}

const currentUserMayImpersonateUser = ({
  currentUserId,
  currentUserIsInAdminRole,
  currentUserIsPhpAdmin,
  impersonationInProgress,
  organizationSubscribedToImpersonationByOrganizationAdminsFeature,
  userId,
  userRole,
}) => {
  if (impersonationInProgress) return false
  if (currentUserId === userId) return false
  if (userRole.toLowerCase() === SUBMITTER_ROLE) return false
  if (currentUserIsPhpAdmin) return true

  return organizationSubscribedToImpersonationByOrganizationAdminsFeature && currentUserIsInAdminRole
}

const UserMenu = ({ user }) => {
  const currentUserId = useSelector(getCurrentUserId)
  const currentUserIsInAdminRole = useSelector(getCurrentUserIsInAdminRole)
  const currentUserIsPhpAdmin = useSelector(getCurrentUserIsPhpAdmin)
  const impersonationInProgress = useSelector(getImpersonationInProgress)
  const organizationSubscribedToImpersonationByOrganizationAdminsFeature = useSelector(
    subscribedToAddOnFeature(IMPERSONATION_BY_ORGANIZATION_ADMINS_FEATURE_NAME),
  )

  const impersonationOfUserPermitted = currentUserMayImpersonateUser({
    currentUserId,
    currentUserIsInAdminRole,
    currentUserIsPhpAdmin,
    impersonationInProgress,
    organizationSubscribedToImpersonationByOrganizationAdminsFeature,
    userId: user.id,
    userRole: user.role,
  })

  return (
    <ExpandMenu
      expandMenuComponent={(
        <MenuButton className="circular-icon-button p-2.5" aria-label="show menu">
          <svg className="!w-4 !h-4" aria-hidden><use xlinkHref="#ellipsis" /></svg>
        </MenuButton>
      )}
    >
      <ExpandMenuItem href={`/admin/users/${user.id}`}>
        View
      </ExpandMenuItem>
      {
        impersonationOfUserPermitted && (
          <ExpandMenuItem onClick={() => impersonateUser(user)}>
            Impersonate
          </ExpandMenuItem>
        )
      }
    </ExpandMenu>
  )
}
UserMenu.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  user: types.object.isRequired,
}

export default UserMenu
